/**
 * NEW DISPLAY CAMPAIGN — /google-ads/awareness/create/campaign
 *
 * Same shape as the App Promotion, YouTube and Search workspaces (step rail ·
 * goal strip · sticky live preview), tuned to the Display Network:
 *
 *   1. Campaign settings — brand (website, business name, logo), name,
 *      bidding (conversion strategies lock without tracking), budget, dates,
 *      locations and languages.
 *   2. Ad groups & ads — MANY ad groups (audience + content targeting), each
 *      with MANY responsive display ads (text, images, logos, videos).
 *   3. Review & save — grouped facts, every issue, and a live build log.
 *
 * Images go to the company's ImageKit library on pick and become Google
 * assets at publish time. Publishing is RESUMABLE: every created entity is
 * remembered by draft id, so a Google rejection can be fixed and re-published
 * without creating anything twice.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, ChevronRight, Globe, Image as ImageIcon, Layers, LayoutTemplate, Lock, Plus, Settings } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import {
  ConversionReadiness, DisplayBidding, googleAdsService, GoogleAdsGeoSuggestion, GoogleAdsLanguage,
} from '@/services/google-ads.service';
import { MEDIA_FOLDERS } from '@/services/uploads.service';
import { BackTo, errMsg, Label, Section, Tag, useYoutubeAuth } from '@/components/google-ads/youtube/youtubeShared';
import { LanguagePicker, LocationSearch } from '@/components/google-ads/youtube/TargetingPickers';
import {
  hostOf, IMAGE_SPECS, ImageDraft, ImageGrid, isHttpUrl, MoneyInput, Pub, today, toMicros,
} from '@/components/google-ads/objectives/objectiveShared';
import {
  BudgetDates, BuildRow, Callout, ChipList, ChoiceCards, ConnectFirst, ConversionNote, CountedInput, Fact, FixList, GoalStrip, IntroBanner,
  PageSpinner, PublishPanel, RailStep, StrengthPill, TargetingFacts, WizardRail, WizardStepPills,
} from '@/components/google-ads/objectives/wizardChrome';
import {
  bidModeForBidding, cloneDisplayGroup, DISPLAY_BIDDING, displayBiddingTitle, DisplayGroupCard, DisplayGroupDraft, displayGroupIssues,
  displayGroupPayload, groupTargetingSummary, newDisplayGroup, publishRda, RDA_LIMITS, RdaBrand, RdaDraft, rdaImageCount, rdaIssues, rdaLogoCount,
  RdaPreview, rdaStrength, writeRdaAsset,
} from '@/components/google-ads/display/displayShared';
import { AWARENESS_OBJECTIVE as CFG } from './awarenessObjective';

type StepKey = 'campaign' | 'groups' | 'review';
const STEPS: ReadonlyArray<RailStep<StepKey>> = [
  { key: 'campaign', label: 'Campaign settings', icon: Settings },
  { key: 'groups', label: 'Ad groups & ads', icon: Layers },
  { key: 'review', label: 'Review & save', icon: CheckCircle2 },
];

const filled = (xs: string[]) => xs.map((x) => x.trim()).filter(Boolean);
type Patch<T> = Partial<T> | ((cur: T) => Partial<T>);

export default function AwarenessCampaignWizard() {
  const navigate = useNavigate();
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const currency = auth?.currency ?? '';
  const [step, setStep] = useState<StepKey>('campaign');

  // ── campaign settings ──
  const [website, setWebsite] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [brandLogo, setBrandLogo] = useState<ImageDraft[]>([]);
  const [name, setName] = useState(`Display - Awareness - ${today()}`);
  const [bidding, setBidding] = useState<DisplayBidding>('MANUAL_CPM');
  const [ceiling, setCeiling] = useState('');
  const [targetCpa, setTargetCpa] = useState('');
  const [budget, setBudget] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [locations, setLocations] = useState<GoogleAdsGeoSuggestion[]>([]);
  const [languages, setLanguages] = useState<GoogleAdsLanguage[]>([]);
  const bidMode = bidModeForBidding(bidding);

  // ── account facts ──
  const [readiness, setReadiness] = useState<ConversionReadiness | null>(null);
  const [takenNames, setTakenNames] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!accountUid) return;
    googleAdsService.display.readiness(accountUid).then(setReadiness).catch(() => setReadiness(null));
    googleAdsService.listCampaigns(accountUid)
      .then((l) => setTakenNames(new Set(l.filter((c) => c.status !== 'REMOVED').map((c) => c.name.trim().toLowerCase()))))
      .catch(() => undefined);
  }, [accountUid]);
  const conversionBlocked = readiness?.conversionBiddingAllowed === false;
  useEffect(() => { if (conversionBlocked && bidding === 'MAXIMIZE_CONVERSIONS') setBidding('MANUAL_CPM'); }, [conversionBlocked, bidding]);

  // ── structure ──
  const siteUrl = isHttpUrl(website) ? website.trim() : '';
  const brand = useMemo<Partial<RdaBrand>>(() => ({ finalUrl: siteUrl, businessName: businessName.trim(), squareLogos: brandLogo.filter((l) => l.url) }), [siteUrl, businessName, brandLogo]);
  const first = useMemo(() => newDisplayGroup(1), []);
  const [groups, setGroups] = useState<DisplayGroupDraft[]>([first]);
  const [openGroup, setOpenGroup] = useState<string>(first.id);
  const [activeAd, setActiveAd] = useState<string>(first.ads[0].id);
  const patchGroup = (gid: string, p: Patch<DisplayGroupDraft>) =>
    setGroups((gs) => gs.map((g) => (g.id === gid ? { ...g, ...(typeof p === 'function' ? p(g) : p) } : g)));
  const patchAd = (gid: string, aid: string, p: Patch<RdaDraft>) =>
    patchGroup(gid, (g) => ({ ads: g.ads.map((a) => (a.id === aid ? { ...a, ...(typeof p === 'function' ? p(a) : p) } : a)) }));

  /** The brand step fills every ad that doesn't have its own landing page / name / logo yet. */
  const applyBrand = () => setGroups((gs) => gs.map((g) => ({
    ...g,
    ads: g.ads.map((a) => ({
      ...a,
      finalUrl: a.finalUrl || brand.finalUrl || '',
      businessName: a.businessName || brand.businessName || '',
      squareLogos: a.squareLogos.length ? a.squareLogos : (brand.squareLogos ?? []).map((i) => ({ ...i })),
    })),
  })));

  // ── publish ──
  const [pub, setPub] = useState<Record<string, Pub>>({});
  const [pubError, setPubError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [finished, setFinished] = useState(false);
  const madeRef = useRef<{ campaign?: string; groups: Record<string, string>; ads: Record<string, string> }>({ groups: {}, ads: {} });
  const campaignLocked = !!madeRef.current.campaign;

  // ── validation ──
  const campaignIssues = useMemo(() => {
    const i: string[] = [];
    if (website.trim() && !siteUrl) i.push('The website must be a full address starting with https://');
    if (businessName.length > RDA_LIMITS.businessName) i.push(`The business name must be ${RDA_LIMITS.businessName} characters or fewer.`);
    if (!name.trim()) i.push('Campaign name is required.');
    else if (!campaignLocked && takenNames.has(name.trim().toLowerCase())) i.push(`A campaign named "${name.trim()}" already exists on this account — Google needs a new name.`);
    if (toMicros(budget) <= 0) i.push('Enter a daily budget.');
    if (start && end && end < start) i.push('The end date is before the start date.');
    if (bidding === 'MAXIMIZE_CONVERSIONS' && conversionBlocked) i.push('Maximize conversions needs conversion tracking, which this account does not have yet — pick another strategy.');
    if (brandLogo.some((l) => !l.url)) i.push('The logo is still uploading.');
    return i;
  }, [website, siteUrl, businessName, name, campaignLocked, takenNames, budget, start, end, bidding, conversionBlocked, brandLogo]);

  const groupIssues = useMemo(() => {
    const i: string[] = [];
    if (groups.length === 0) i.push('Add at least one ad group.');
    groups.forEach((g, n) => {
      const label = g.name.trim() || `Ad group ${n + 1}`;
      const gi = displayGroupIssues(g, bidMode);
      if (gi.length) i.push(`${label}: needs ${gi.join(', ')}.`);
      g.ads.forEach((a, k) => {
        const ai = rdaIssues(a);
        if (ai.length) i.push(`${label} → ${a.name.trim() || `Ad ${k + 1}`}: needs ${ai.join(', ')}.`);
      });
    });
    return i;
  }, [groups, bidMode]);

  const allIssues = [...campaignIssues, ...groupIssues];
  const issuesFor = (k: StepKey) => (k === 'campaign' ? campaignIssues.length : k === 'groups' ? groupIssues.length : 0);

  const allAds = useMemo(() => groups.flatMap((g) => g.ads.map((a) => ({ g, a }))), [groups]);
  const active = allAds.find((x) => x.a.id === activeAd) ?? allAds[0] ?? null;
  const imageCount = allAds.reduce((n, x) => n + rdaImageCount(x.a), 0);
  const biddingSub = bidding === 'MAXIMIZE_CLICKS' && toMicros(ceiling) > 0 ? `Max CPC ${ceiling} ${currency}`
    : bidding === 'MAXIMIZE_CONVERSIONS' && toMicros(targetCpa) > 0 ? `Target CPA ${targetCpa} ${currency}`
    : bidMode === 'cpm' ? 'Max CPM set per ad group' : bidMode === 'cpc' ? 'Max CPC set per ad group' : 'No cap';

  // ── publish (resumable) ──
  const publish = async () => {
    if (!accountUid || allIssues.length) return;
    setPublishing(true); setPubError(null);
    const mark = (k: string, v: Pub) => setPub((m) => ({ ...m, [k]: v }));
    const made = madeRef.current;
    try {
      if (!made.campaign) {
        mark('campaign', 'running');
        try {
          const r = await googleAdsService.display.createCampaign(accountUid, {
            name: name.trim(), dailyBudgetMicros: toMicros(budget), status: 'PAUSED', bidding,
            cpcBidCeilingMicros: bidding === 'MAXIMIZE_CLICKS' && toMicros(ceiling) > 0 ? toMicros(ceiling) : undefined,
            targetCpaMicros: bidding === 'MAXIMIZE_CONVERSIONS' && toMicros(targetCpa) > 0 ? toMicros(targetCpa) : undefined,
            startDate: start || undefined, endDate: end || undefined,
            geoTargetIds: locations.length ? locations.map((l) => l.id) : undefined,
            languageIds: languages.length ? languages.map((l) => l.id) : undefined,
          });
          made.campaign = r.campaignId;
        } catch (e) { mark('campaign', 'failed'); setPubError(`Campaign: ${errMsg(e)}`); return; }
        mark('campaign', 'done');
      }
      for (const g of groups) {
        if (!made.groups[g.id]) {
          mark(g.id, 'running');
          try {
            const r = await googleAdsService.display.createAdGroup(accountUid, displayGroupPayload(made.campaign!, g, bidMode));
            made.groups[g.id] = r.adGroupId;
          } catch (e) { mark(g.id, 'failed'); setPubError(`Ad group "${g.name.trim()}": ${errMsg(e)}`); return; }
          mark(g.id, 'done');
        }
        for (const a of g.ads) {
          if (made.ads[a.id]) continue;
          mark(a.id, 'running');
          try {
            const r = await publishRda(accountUid, made.groups[g.id], a, (field, id, rn) => patchAd(g.id, a.id, writeRdaAsset(field, id, rn)));
            made.ads[a.id] = r.resourceName;
          } catch (e) { mark(a.id, 'failed'); setPubError(`Ad "${a.name.trim() || filled(a.headlines)[0] || 'untitled'}" in ${g.name.trim()}: ${errMsg(e)}`); return; }
          mark(a.id, 'done');
        }
      }
      setFinished(true);
      toast.success('Display campaign published — everything is paused, so nothing spends until you enable it.');
      setTimeout(() => navigate(CFG.base), 1200);
    } finally { setPublishing(false); }
  };

  const addGroup = () => {
    const g = newDisplayGroup(groups.length + 1, brand);
    setGroups((gs) => [...gs, g]); setOpenGroup(g.id); setActiveAd(g.ads[0].id);
  };

  const buildRows: BuildRow[] = [
    { key: 'campaign', label: `Campaign — ${name || 'untitled'}`, level: 0, state: pub.campaign },
    ...groups.flatMap((g, n): BuildRow[] => [
      { key: g.id, label: `Ad group — ${g.name.trim() || `Ad group ${n + 1}`}`, level: 1, state: pub[g.id], detail: groupTargetingSummary(g) },
      ...g.ads.map((a, k): BuildRow => ({
        key: a.id, label: `Ad — ${filled(a.headlines)[0] || a.name || `Ad ${k + 1}`}`, level: 2, state: pub[a.id],
        detail: `${rdaImageCount(a)} image${rdaImageCount(a) === 1 ? '' : 's'}${rdaLogoCount(a) ? ` · ${rdaLogoCount(a)} logo${rdaLogoCount(a) === 1 ? '' : 's'}` : ''}`,
      })),
    ]),
  ];

  return (
    <>
      <Topbar title={`New ${CFG.title} campaign`} breadcrumb={`Google Ads / ${CFG.title}`}
        description="Display campaign, audiences and responsive display ads — then save everything paused"
        back={<BackTo to={CFG.base} label={`Back to ${CFG.title}`} />} />

      <div className="w-full px-7 py-7">
        {auth === undefined && <PageSpinner />}
        {auth !== undefined && !ready && <ConnectFirst what="Display campaigns" />}

        {ready && (
          <div className="flex w-full gap-5 pb-12">
            <WizardRail steps={STEPS} step={step} onStep={setStep} issues={issuesFor} summary={(
              <>
                <div>{groups.length} ad group{groups.length === 1 ? '' : 's'}</div>
                <div>{allAds.length} ad{allAds.length === 1 ? '' : 's'} · {imageCount} image{imageCount === 1 ? '' : 's'}</div>
                {active && <div className="flex items-center gap-1.5 pt-1">Ad strength <StrengthPill s={rdaStrength(active.a)} /></div>}
              </>
            )} />

            <div className="min-w-0 flex-1 space-y-4">
              <WizardStepPills steps={STEPS} step={step} onStep={setStep} />
              <GoalStrip items={[
                { label: 'Goal', value: CFG.goal },
                { label: 'Type', value: 'Display' },
                { label: 'Served via', value: 'Display Network · YouTube · Gmail' },
              ]}>
                <Tag>starts paused</Tag>
              </GoalStrip>

              {/* ── STEP 1: campaign ── */}
              {step === 'campaign' && (
                <>
                  {campaignLocked && (
                    <Callout icon={Lock}>The campaign already exists on Google, so its settings are locked. Fix the failing ad group or ad and publish again.</Callout>
                  )}
                  <fieldset disabled={campaignLocked} className="min-w-0 space-y-4">
                    <IntroBanner icon={LayoutTemplate} title="Awareness & reach on the Google Display Network"
                      points={['Responsive display ads', 'Audience + content targeting', 'Every banner size from one ad', 'Starts paused']}>
                      Upload a few images and write a handful of lines once — Google assembles them into every banner size and shows them
                      across millions of websites, apps, Gmail and YouTube to the people you choose.
                    </IntroBanner>

                    <Section title="Your brand" hint="Every new ad starts with these — you can still change them on any ad.">
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <Label>Website</Label>
                          <Input className="mt-1.5" iconLeft={<Globe size={14} />} placeholder="https://yourbusiness.com" value={website}
                            onChange={(e) => setWebsite(e.target.value)} onBlur={applyBrand} />
                          {siteUrl && <p className="mt-1 text-2xs text-success">{hostOf(siteUrl)} — ads link here unless you give one its own page.</p>}
                        </div>
                        <div>
                          <Label>Business name</Label>
                          <CountedInput className="mt-1.5" value={businessName} maxLen={RDA_LIMITS.businessName} placeholder="Shown on every ad"
                            onChange={setBusinessName} ariaLabel="Business name" />
                        </div>
                      </div>
                      <div className="mt-4 max-w-md">
                        <ImageGrid label="Logo" spec={IMAGE_SPECS.logo} images={brandLogo} max={1} optional folder={MEDIA_FOLDERS.googleDisplay}
                          onChange={(f) => setBrandLogo((cur) => f(cur))} />
                      </div>
                    </Section>

                    <Section title="Campaign name">
                      <Input value={name} maxLength={255} onChange={(e) => setName(e.target.value)} />
                    </Section>

                    <Section title="Bidding" hint="How you pay. Awareness campaigns usually pay for impressions people actually see.">
                      <ConversionNote readiness={readiness} />
                      <ChoiceCards options={DISPLAY_BIDDING} value={bidding} onChange={setBidding} recommended="MANUAL_CPM" conversionBlocked={conversionBlocked} />
                      {(bidding === 'MAXIMIZE_CLICKS' || bidding === 'MAXIMIZE_CONVERSIONS') && (
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {bidding === 'MAXIMIZE_CLICKS' ? (
                            <div>
                              <Label>Max CPC cap <span className="font-normal text-text-muted">(optional)</span></Label>
                              <div className="mt-1.5"><MoneyInput value={ceiling} onChange={setCeiling} currency={currency} placeholder="No cap" /></div>
                              <p className="mt-1 text-2xs text-text-muted">The most Google may pay for one click.</p>
                            </div>
                          ) : (
                            <div>
                              <Label>Target CPA <span className="font-normal text-text-muted">(optional)</span></Label>
                              <div className="mt-1.5"><MoneyInput value={targetCpa} onChange={setTargetCpa} currency={currency} placeholder="Let Google decide" /></div>
                              <p className="mt-1 text-2xs text-text-muted">Leave blank for the first weeks — Google learns faster without a target.</p>
                            </div>
                          )}
                        </div>
                      )}
                      {bidMode !== 'none' && <p className="mt-3 text-2xs text-text-muted">You set the {bidMode === 'cpm' ? 'max CPM' : 'max CPC'} on each ad group in the next step.</p>}
                    </Section>

                    <Section title="Budget and dates">
                      <BudgetDates budget={budget} onBudget={setBudget} start={start} onStart={setStart} end={end} onEnd={setEnd} currency={currency} />
                    </Section>

                    <Section title="Locations" hint="Searched live from Google — countries, regions and cities. Empty = everywhere Google serves.">
                      <LocationSearch accountUid={accountUid} value={locations} onChange={setLocations} />
                    </Section>

                    <Section title="Languages" hint="Languages your audience browses in. Empty = all languages.">
                      <LanguagePicker accountUid={accountUid} value={languages} onChange={setLanguages} />
                    </Section>
                  </fieldset>

                  <div className="flex justify-end">
                    <Button onClick={() => { applyBrand(); setStep('groups'); }} iconRight={<ChevronRight size={15} />}>Next: Ad groups & ads</Button>
                  </div>
                </>
              )}

              {/* ── STEP 2: ad groups + ads ── */}
              {step === 'groups' && (
                <>
                  <Callout icon={ImageIcon}>
                    One ad group per audience. Each ad needs a landscape and a square image, at least one headline, a long headline and a description —
                    images of any shape are cropped to Google's ratios automatically, logos are padded so nothing is cut off.
                  </Callout>

                  {groups.map((g, n) => (
                    <DisplayGroupCard key={g.id} group={g} index={n} open={openGroup === g.id} onToggle={() => setOpenGroup(openGroup === g.id ? '' : g.id)}
                      bidMode={bidMode} currency={currency} pub={pub} madeGroup={!!madeRef.current.groups[g.id]} madeAd={(id) => !!madeRef.current.ads[id]}
                      busy={publishing} onChange={(p) => patchGroup(g.id, p)} brand={brand}
                      onDuplicate={() => {
                        const c = cloneDisplayGroup(g);
                        setGroups((gs) => { const i = gs.findIndex((x) => x.id === g.id); return [...gs.slice(0, i + 1), c, ...gs.slice(i + 1)]; });
                        setOpenGroup(c.id); if (c.ads[0]) setActiveAd(c.ads[0].id);
                      }}
                      onRemove={groups.length > 1 ? () => setGroups((gs) => gs.filter((x) => x.id !== g.id)) : undefined}
                      activeAdId={active?.a.id} onFocusAd={setActiveAd} />
                  ))}

                  <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} disabled={publishing} onClick={addGroup}>Add another ad group</Button>

                  <div className="flex justify-between">
                    <Button variant="secondary" onClick={() => setStep('campaign')}>Back</Button>
                    <Button onClick={() => setStep('review')} iconRight={<ChevronRight size={15} />}>Next: Review</Button>
                  </div>
                </>
              )}

              {/* ── STEP 3: review ── */}
              {step === 'review' && (
                <>
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.1fr_1fr]">
                    <Section title="Campaign" hint="Everything Google will receive for the campaign itself."
                      right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" onClick={() => setStep('campaign')}>Edit</Button>}>
                      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                        <Fact label="Name" value={name || '—'} wide />
                        <Fact label="Goal" value={CFG.title} sub={CFG.goal} />
                        <Fact label="Bidding" value={displayBiddingTitle(bidding)} sub={biddingSub} />
                        <Fact label="Daily budget" value={budget ? `${budget} ${currency}` : '—'}
                          sub={toMicros(budget) > 0 ? `≈ ${(Number(budget) * 30.4).toFixed(0)} ${currency} / month` : undefined} />
                        <Fact label="Schedule" value={start ? `${start} → ${end || 'no end'}` : `Starts when enabled${end ? ` → ends ${end}` : ' · no end date'}`} />
                        <Fact label="Networks" value="Google Display Network" sub="Sites, apps, Gmail and YouTube placements" wide />
                        {siteUrl && <Fact label="Website" value={siteUrl} wide />}
                      </div>
                      <TargetingFacts locations={locations.map((l) => l.name)} languages={languages.map((l) => l.name)} />
                    </Section>

                    <Section title="Ad groups & ads" hint={`${groups.length} ad group${groups.length === 1 ? '' : 's'} · ${allAds.length} ad${allAds.length === 1 ? '' : 's'} · ${imageCount} image${imageCount === 1 ? '' : 's'}`}
                      right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" onClick={() => setStep('groups')}>Edit</Button>}>
                      <div className="space-y-2.5">
                        {groups.map((g, n) => (
                          <div key={g.id} className="rounded-lg border border-border p-2.5">
                            <div className="flex items-center gap-2">
                              <Layers size={13} className="shrink-0 text-text-muted" />
                              <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-primary">{g.name.trim() || `Ad group ${n + 1}`}</span>
                              {bidMode !== 'none' && <span className="shrink-0 text-2xs text-text-muted">{bidMode === 'cpm' ? 'max CPM' : 'max CPC'} {g.bid}</span>}
                            </div>
                            <div className="mt-1.5 pl-5"><ChipList items={groupTargetingSummary(g).split(' · ')} empty="Everyone" max={6} /></div>
                            <div className="mt-2 space-y-1 pl-5">
                              {g.ads.map((a, k) => {
                                const thumb = a.marketingImages.find((i) => i.url)?.url ?? a.squareImages.find((i) => i.url)?.url;
                                return (
                                  <button key={a.id} type="button" onClick={() => setActiveAd(a.id)}
                                    className="flex w-full items-center gap-2 rounded px-1 py-1 text-left hover:bg-surface-alt">
                                    {thumb ? <img src={thumb} alt="" className="h-6 w-11 shrink-0 rounded object-cover" /> : <span className="h-6 w-11 shrink-0 rounded bg-surface-alt" />}
                                    <span className="min-w-0 flex-1 truncate text-[12px] text-text-primary">{filled(a.headlines)[0] || a.name || `Ad ${k + 1}`}</span>
                                    <StrengthPill s={rdaStrength(a)} />
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </Section>
                  </div>

                  <FixList groups={[{ step: 'campaign' as StepKey, issues: campaignIssues }, { step: 'groups' as StepKey, issues: groupIssues }]} onJump={setStep} />

                  <PublishPanel
                    hint="Creates everything on Google in order — campaign, ad groups, image assets, ads. All of it starts paused, so nothing spends until you enable the campaign."
                    rows={buildRows} error={pubError} publishing={publishing} blocked={allIssues.length > 0} finished={finished}
                    onPublish={publish} onCancel={() => navigate(CFG.base)} />
                </>
              )}
            </div>

            {/* live preview */}
            <div className="hidden w-[320px] shrink-0 xl:block">
              <div className="sticky top-24"><RdaPreview ad={active?.a ?? null} /></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
